// src/pages/Signup.jsx
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import Terms from "./Terms";
import { getAuth } from "firebase/auth";
import axios from "axios";

const SITE_KEY_V2 = process.env.REACT_APP_RECAPTCHA_SITE_KEY_V2;

const Signup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = getAuth();
  const recaptchaRef = useRef(null);

  const [firstName, setFirstName] = useState(localStorage.getItem("signup_firstName") || "");
  const [middleName, setMiddleName] = useState(localStorage.getItem("signup_middleName") || "");
  const [lastName, setLastName] = useState(localStorage.getItem("signup_lastName") || "");
  const [email, setEmail] = useState(localStorage.getItem("signup_email") || "");
  const [countryCode, setCountryCode] = useState(localStorage.getItem("signup_countryCode") || "1");
  const [phoneNumber, setPhoneNumber] = useState(localStorage.getItem("signup_phoneNumber") || "");
  const [language, setLanguage] = useState(localStorage.getItem("signup_language") || "");
  const [userType, setUserType] = useState(localStorage.getItem("signup_userType") || "");
  const [companyName, setCompanyName] = useState(localStorage.getItem("signup_companyName") || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [phoneErrorText, setPhoneErrorText] = useState("");
  const [formError, setFormError] = useState("");
  const [existingAccount, setExistingAccount] = useState(false);
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    title: "",
    message: "",
    nextPath: "",
  });
  const [fieldErrors, setFieldErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    userType: "",
    influencerProfile1: "",
  });

  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");

  // ⭐ Existing private access key state
  const [promoCode, setPromoCode] = useState(localStorage.getItem("signup_promoCode") || "");

  // ⭐ NEW: influencer application state
  const [isInfluencer, setIsInfluencer] = useState(
    localStorage.getItem("signup_isInfluencer") === "yes" ? "yes" : "no"
  );
  const [influencerProfile1, setInfluencerProfile1] = useState(localStorage.getItem("signup_influencerProfile1") || "");
  const [influencerProfile2, setInfluencerProfile2] = useState(localStorage.getItem("signup_influencerProfile2") || "");
  const [influencerProfile3, setInfluencerProfile3] = useState(localStorage.getItem("signup_influencerProfile3") || "");

  // ✅ Detect phones/tablets to widen the layout
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1024px)");
    const apply = () => setIsSmallScreen(mq.matches);
    apply();
    mq.addEventListener?.("change", apply);
    return () => mq.removeEventListener?.("change", apply);
  }, []);

  useEffect(() => {
    validatePassword(password);
  }, [password]);

  useEffect(() => {
    localStorage.setItem("signup_firstName", firstName);
    localStorage.setItem("signup_middleName", middleName);
    localStorage.setItem("signup_lastName", lastName);
    localStorage.setItem("signup_email", email);
    localStorage.setItem("signup_countryCode", countryCode);
    localStorage.setItem("signup_phoneNumber", phoneNumber);
    localStorage.setItem("signup_language", language);
    localStorage.setItem("signup_userType", userType);
    localStorage.setItem("signup_companyName", companyName);
    localStorage.setItem("signup_promoCode", promoCode);
    localStorage.setItem("signup_isInfluencer", isInfluencer);
    localStorage.setItem("signup_influencerProfile1", influencerProfile1);
    localStorage.setItem("signup_influencerProfile2", influencerProfile2);
    localStorage.setItem("signup_influencerProfile3", influencerProfile3);
  }, [
    firstName,
    middleName,
    lastName,
    email,
    countryCode,
    phoneNumber,
    language,
    userType,
    companyName,
    promoCode,
    isInfluencer,
    influencerProfile1,
    influencerProfile2,
    influencerProfile3,
  ]);

  const validatePassword = (pwd) => {
    const isValid =
      pwd.length >= 8 &&
      /[A-Z]/.test(pwd) &&
      /[a-z]/.test(pwd) &&
      /[0-9]/.test(pwd) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(pwd);
    setPasswordError(!isValid);
  };

  const validateAndFormatPhone = (countryCodeRaw, phoneRaw) => {
    const ccDigits = (countryCodeRaw || "").replace(/\D/g, "");
    const phoneDigitsRaw = (phoneRaw || "").replace(/\D/g, "");
    const hasAnyInput = ccDigits.length > 0 || phoneDigitsRaw.length > 0;

    // Phone is optional: if both empty, accept null
    if (!hasAnyInput) {
      return { phoneE164: null, error: null };
    }

    if (!ccDigits || !phoneDigitsRaw) {
      return {
        phoneE164: null,
        error: "Please enter both country code and phone number, or leave them both blank.",
      };
    }

    let phoneDigits = phoneDigitsRaw;

    // Special handling for U.S. numbers
    if (ccDigits === "1") {
      if (phoneDigits.length === 11 && phoneDigits.startsWith("1")) {
        phoneDigits = phoneDigits.slice(1);
      }
      if (phoneDigits.length !== 10) {
        return {
          phoneE164: null,
          error: "Please enter a valid 10-digit U.S. phone number.",
        };
      }
    }

    const phoneE164 = `+${ccDigits}${phoneDigits}`;
    return { phoneE164, error: null };
  };

  const mapUserType = (label) => {
    switch (label) {
      case "Individual": return "individual";
      case "Attorney": return "attorney";
      case "Law Firm": return "law_firm";
      case "Paralegal Firm": return "paralegal_firm";
      default: return "";
    }
  };

  const buildProfile = (phoneE164) => ({
    first_name: firstName,
    middle_name: middleName,
    last_name: lastName,
    email,
    phone: phoneE164 || null,
    language,
    user_type: mapUserType(userType),
    company_name: companyName || null,
    password,
    plan_type: "free",
    is_verified: false,
    address: {
      address1,
      address2,
      city,
      state_province: stateProvince,
      postal_code: postalCode,
      country,
    },
    promo_code: promoCode || "",
    is_influencer: isInfluencer === "yes",
    influencer_profiles:
      isInfluencer === "yes"
        ? [influencerProfile1, influencerProfile2, influencerProfile3]
            .map((value) => value.trim())
            .filter(Boolean)
        : [],
  });

  const resetForm = () => {
    setFirstName("");
    setMiddleName("");
    setLastName("");
    setEmail("");
    setCountryCode("1");
    setPhoneNumber("");
    setLanguage("");
    setUserType("");
    setCompanyName("");
    setPassword("");
    setConfirmPassword("");
    setAddress1("");
    setAddress2("");
    setCity("");
    setStateProvince("");
    setPostalCode("");
    setCountry("");
    setAgreeTerms(false);
    setShowTerms(false);
    setPhoneError(false);
    setPhoneErrorText("");
    setFormError("");
    setFieldErrors({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      userType: "",
      influencerProfile1: "",
    });
    setPromoCode("");
    setIsInfluencer("no");
    setInfluencerProfile1("");
    setInfluencerProfile2("");
    setInfluencerProfile3("");
  };

  const getFreshToken = async () => {
    if (!recaptchaRef.current) throw new Error("captcha-not-ready");
    const EXEC_TIMEOUT_MS = 15000;

    const execPromise = recaptchaRef.current.executeAsync();
    const timeoutPromise = new Promise((_, rej) =>
      setTimeout(() => rej(new Error("captcha-timeout")), EXEC_TIMEOUT_MS)
    );

    try {
      const token = await Promise.race([execPromise, timeoutPromise]);
      return token;
    } finally {
      try {
        recaptchaRef.current.reset();
      } catch {}
    }
  };

  const openSuccessDialog = ({ title, message, nextPath }) => {
    setSuccessDialog({
      open: true,
      title: title || "Success",
      message: message || "",
      nextPath: nextPath || "",
    });
  };

  const closeSuccessDialog = () => {
    const nextPath = successDialog.nextPath;

    setSuccessDialog({
      open: false,
      title: "",
      message: "",
      nextPath: "",
    });

    if (nextPath) {
      navigate(nextPath);
    }
  };

  const handleSignup = async () => {
    console.log("🚀 Triggering signup process...");

    setFormError("");
    setExistingAccount(false);
    setPhoneError(false);
    setPhoneErrorText("");
    setFieldErrors({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      userType: "",
      influencerProfile1: "",
    });

    const newFieldErrors = {};
    if (!firstName) newFieldErrors.firstName = "First name is required.";
    if (!lastName) newFieldErrors.lastName = "Last name is required.";
    if (!email) newFieldErrors.email = "Email is required.";
    if (!password) newFieldErrors.password = "Password is required.";
    if (!confirmPassword) newFieldErrors.confirmPassword = "Confirm password is required.";
    if (!userType) newFieldErrors.userType = "Please choose what best describes you.";

    if (isInfluencer === "yes" && !influencerProfile1.trim()) {
      newFieldErrors.influencerProfile1 = "Please provide at least one social media profile or website.";
    }

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setFormError("Please fill in all required fields (marked with *).");
      return;
    }

    if (password !== confirmPassword) {
      setFormError("Passwords do not match.");
      return;
    }

    if (passwordError) {
      setFormError("Password does not meet the required criteria.");
      return;
    }

    if (!agreeTerms) {
      setFormError("You must agree to the Terms of Use.");
      return;
    }

    if (!SITE_KEY_V2) {
      setFormError("reCAPTCHA not configured.");
      return;
    }

    const { phoneE164, error: phoneValidationError } = validateAndFormatPhone(countryCode, phoneNumber);
    if (phoneValidationError) {
      setPhoneError(true);
      setPhoneErrorText(phoneValidationError);
      setFormError("Please correct the errors above and try again.");
      return;
    }

    setLoading(true);
    try {
      const recaptchaToken = await getFreshToken();

      const submittedEmail = email.trim().toLowerCase();
      const profile = buildProfile(phoneE164);
      profile.email = submittedEmail;
      profile.email_action_link = `${process.env.REACT_APP_FRONTEND_URL}/complete-signup?email=${encodeURIComponent(submittedEmail)}`;
      profile.recaptchaToken = recaptchaToken;

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/auth/create-user`,
        profile
      );

      const responseData = response?.data || {};
      const responseEmail = String(responseData.email || submittedEmail).trim().toLowerCase();

      if (responseData.signup_completed === true) {
        localStorage.clear();

        openSuccessDialog({
          title: "Signup Complete",
          message:
            responseData.message ||
            "Your email is already verified and your signup is complete. You can sign in.",
          nextPath: "/login",
        });
        return;
      }

      localStorage.clear();
      localStorage.setItem("signup_email", responseEmail);

      openSuccessDialog({
        title: "Check Your Email",
        message:
          responseData.message ||
          `A verification link has been sent to ${responseEmail}.`,
        nextPath: "/check-your-email",
      });
    } catch (err) {
      console.error("❌ Error during signup:", err.response?.data || err.message);
      let message = "Signup failed. Please try again later.";

      const em = String(err?.message || "");
      if (em.includes("captcha") || em.includes("Timeout")) {
        message = "reCAPTCHA expired or failed. Please try again.";
      } else if (err.response?.data) {
        if (typeof err.response.data === "string") {
          message = err.response.data;
        } else if (err.response.data.error) {
          message = err.response.data.error;
        } else if (err.response.data.message) {
          message = err.response.data.message;
        }

        if (err.response.data.already_verified === true) {
          setExistingAccount(true);
        }
      }

      setFormError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ ...styles.outerWrap, ...(isSmallScreen ? styles.outerWrapCompact : null) }}>
      <div style={{ ...styles.card, ...(isSmallScreen ? styles.cardWide : null) }}>
        <h2 style={styles.title}>Signup</h2>

        <div style={styles.form}>
          <input
            type="text"
            placeholder="First Name *"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            style={{
              ...styles.input,
              borderColor: fieldErrors.firstName ? "#b00020" : "#ccc",
            }}
          />
          {fieldErrors.firstName && (
            <p style={styles.inlineError}>{fieldErrors.firstName}</p>
          )}

          <input
            type="text"
            placeholder="Middle Name (optional)"
            value={middleName}
            onChange={(e) => setMiddleName(e.target.value)}
            style={styles.input}
          />

          <input
            type="text"
            placeholder="Last Name *"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            style={{
              ...styles.input,
              borderColor: fieldErrors.lastName ? "#b00020" : "#ccc",
            }}
          />
          {fieldErrors.lastName && (
            <p style={styles.inlineError}>{fieldErrors.lastName}</p>
          )}

          <input
            type="email"
            placeholder="Email *"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              ...styles.input,
              borderColor: fieldErrors.email ? "#b00020" : "#ccc",
            }}
          />
          {fieldErrors.email && (
            <p style={styles.inlineError}>{fieldErrors.email}</p>
          )}

          <div style={styles.phoneRow}>
            <input
              type="text"
              placeholder="Country Code"
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              style={{ ...styles.input, maxWidth: 140 }}
            />
            <input
              type="text"
              placeholder="Phone Number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              style={styles.input}
            />
          </div>
          {phoneError && (
            <p style={styles.inlineError}>
              {phoneErrorText || "Please enter a valid phone number."}
            </p>
          )}

          <input
            type="text"
            placeholder="Preferred Language"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            style={styles.input}
          />

          <select
            value={userType}
            onChange={(e) => setUserType(e.target.value)}
            style={{
              ...styles.input,
              borderColor: fieldErrors.userType ? "#b00020" : "#ccc",
            }}
          >
            <option value="">What best describes you? *</option>
            <option value="Individual">Individual (Not a legal professional)</option>
            <option value="Attorney">Attorney</option>
            <option value="Law Firm">Law Firm</option>
            <option value="Paralegal Firm">Paralegal Firm</option>
          </select>
          {fieldErrors.userType && (
            <p style={styles.inlineError}>{fieldErrors.userType}</p>
          )}

          <div style={styles.radioGroupWrap}>
            <p style={styles.radioGroupLabel}>Are you applying as an influencer? *</p>
            <div style={styles.radioRow}>
              <label style={styles.radioOption}>
                <input
                  type="radio"
                  name="isInfluencer"
                  value="no"
                  checked={isInfluencer === "no"}
                  onChange={(e) => setIsInfluencer(e.target.value)}
                />
                <span style={styles.radioText}>No</span>
              </label>

              <label style={styles.radioOption}>
                <input
                  type="radio"
                  name="isInfluencer"
                  value="yes"
                  checked={isInfluencer === "yes"}
                  onChange={(e) => setIsInfluencer(e.target.value)}
                />
                <span style={styles.radioText}>Yes</span>
              </label>
            </div>
          </div>

          {isInfluencer === "yes" && (
            <div style={styles.influencerBox}>
              <input
                type="text"
                placeholder="Primary social media profile or website *"
                value={influencerProfile1}
                onChange={(e) => setInfluencerProfile1(e.target.value)}
                style={{
                  ...styles.input,
                  borderColor: fieldErrors.influencerProfile1 ? "#b00020" : "#ccc",
                }}
              />
              {fieldErrors.influencerProfile1 && (
                <p style={styles.inlineError}>{fieldErrors.influencerProfile1}</p>
              )}

              <input
                type="text"
                placeholder="Additional social media profile or website (optional)"
                value={influencerProfile2}
                onChange={(e) => setInfluencerProfile2(e.target.value)}
                style={styles.input}
              />

              <input
                type="text"
                placeholder="Additional social media profile or website (optional)"
                value={influencerProfile3}
                onChange={(e) => setInfluencerProfile3(e.target.value)}
                style={styles.input}
              />

              <p style={styles.helperText}>
                Please provide at least one social media profile or website.
              </p>
            </div>
          )}

          {(userType === "Attorney" || userType === "Law Firm" || userType === "Paralegal Firm") && (
            <input
              type="text"
              placeholder="Company Name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              style={styles.input}
            />
          )}

          <input
            type="text"
            placeholder="Street Address"
            value={address1}
            onChange={(e) => setAddress1(e.target.value)}
            style={styles.input}
          />
          <input
            type="text"
            placeholder="Apt, Suite, etc. (optional)"
            value={address2}
            onChange={(e) => setAddress2(e.target.value)}
            style={styles.input}
          />
          <input
            type="text"
            placeholder="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            style={styles.input}
          />
          <input
            type="text"
            placeholder="State / Province"
            value={stateProvince}
            onChange={(e) => setStateProvince(e.target.value)}
            style={styles.input}
          />
          <input
            type="text"
            placeholder="Postal Code / Zip Code"
            value={postalCode}
            onChange={(e) => setPostalCode(e.target.value)}
            style={styles.input}
          />
          <input
            type="text"
            placeholder="Country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            style={styles.input}
          />

          <input
            type="password"
            placeholder={`${t("password")} *`}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{
              ...styles.input,
              borderColor: fieldErrors.password ? "#b00020" : "#ccc",
            }}
          />
          {password && (
            <p style={{ color: passwordError ? "#b00020" : "#333", fontSize: 13, marginTop: -4 }}>
              Password must be at least 8 characters and include uppercase, lowercase, number, and special character.
            </p>
          )}
          {fieldErrors.password && (
            <p style={styles.inlineError}>{fieldErrors.password}</p>
          )}

          <input
            type="password"
            placeholder={`${t("confirm_password")} *`}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={{
              ...styles.input,
              borderColor: fieldErrors.confirmPassword ? "#b00020" : "#ccc",
            }}
          />
          {fieldErrors.confirmPassword && (
            <p style={styles.inlineError}>{fieldErrors.confirmPassword}</p>
          )}

          <input
            type="text"
            placeholder="Private Access Key (optional)"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value)}
            style={styles.input}
          />

          <label style={styles.checkboxContainer}>
            <input
              type="checkbox"
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
            />
            <span style={{ marginLeft: 8 }}>
              I agree to the{" "}
              <button onClick={() => setShowTerms(!showTerms)} style={styles.linkButton}>
                Terms of Use
              </button>
              {" "}*
            </span>
          </label>

          {showTerms && (
            <div style={styles.termsBox}>
              <Terms />
            </div>
          )}

          <div style={{ height: 0, overflow: "hidden" }}>
            <ReCAPTCHA ref={recaptchaRef} sitekey={SITE_KEY_V2} size="invisible" />
          </div>

          {formError && (
            <p style={styles.formError}>{formError}</p>
          )}

          {existingAccount && (
            <div style={styles.accountHelp}>
              <Link to="/login" style={styles.accountHelpLink}>Sign in</Link>
              <span>or</span>
              <Link to="/forgot-password" style={styles.accountHelpLink}>Reset password</Link>
            </div>
          )}

          <button onClick={handleSignup} style={styles.button} disabled={loading}>
            {loading ? "Signing up..." : "Signup"}
          </button>

          <div style={styles.footerLinks}>
            <Link to="/terms" style={styles.footerLink}>Terms of Use</Link>
            <span style={{ margin: "0 10px" }}>|</span>
            <Link to="/privacy" style={styles.footerLink}>Privacy Policy</Link>
          </div>
        </div>
      </div>

      {successDialog.open && (
        <div style={styles.dialogOverlay} role="presentation">
          <div
            style={styles.dialogCard}
            role="dialog"
            aria-modal="true"
            aria-labelledby="signup-success-dialog-title"
          >
            <div style={styles.dialogIcon}>✓</div>

            <h3 id="signup-success-dialog-title" style={styles.dialogTitle}>
              {successDialog.title}
            </h3>

            <p style={styles.dialogMessage}>{successDialog.message}</p>

            <button
              type="button"
              onClick={closeSuccessDialog}
              style={styles.dialogButton}
              autoFocus
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  outerWrap: {
    maxWidth: 960,
    margin: "24px auto",
    padding: "0 16px 40px",
    boxSizing: "border-box",
  },
  outerWrapCompact: {
    padding: "0 8px 40px",
  },
  card: {
    maxWidth: 440,
    width: "100%",
    margin: "0 auto",
    padding: 28,
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    display: "flex",
    flexDirection: "column",
    fontFamily: "sans-serif",
    boxSizing: "border-box",
  },
  cardWide: {
    maxWidth: 520,
  },
  title: {
    textAlign: "center",
    fontSize: 26,
    fontWeight: "bold",
    marginBottom: 20,
    color: "#111827",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
    alignItems: "stretch",
  },
  input: {
    width: "100%",
    padding: 12,
    fontSize: 16,
    borderRadius: 10,
    border: "1px solid #ccc",
    outline: "none",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    boxSizing: "border-box",
  },
  phoneRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
  },
  radioGroupWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: -2,
  },
  radioGroupLabel: {
    margin: 0,
    fontSize: 14,
    fontWeight: 600,
    color: "#111827",
  },
  radioRow: {
    display: "flex",
    gap: 18,
    alignItems: "center",
    flexWrap: "wrap",
  },
  radioOption: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#111827",
    cursor: "pointer",
  },
  radioText: {
    lineHeight: 1,
  },
  influencerBox: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: 12,
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    backgroundColor: "#fafafa",
  },
  helperText: {
    fontSize: 12,
    color: "#555",
    margin: 0,
  },
  inlineError: {
    color: "#b00020",
    fontSize: 13,
    marginTop: -2,
  },
  formError: {
    color: "#b00020",
    fontSize: 14,
    marginTop: 8,
    marginBottom: 4,
    textAlign: "center",
  },
  accountHelp: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    marginTop: -4,
  },
  accountHelpLink: {
    color: "#1E2A78",
    textDecoration: "underline",
    fontWeight: 600,
  },
  checkboxContainer: {
    fontSize: 14,
    display: "flex",
    alignItems: "flex-start",
  },
  linkButton: {
    background: "none",
    border: "none",
    color: "#1E2A78",
    textDecoration: "underline",
    cursor: "pointer",
    padding: 0,
    fontSize: 14,
  },
  termsBox: {
    border: "1px solid #ccc",
    padding: 10,
    maxHeight: 200,
    overflowY: "auto",
    backgroundColor: "#f9f9f9",
    fontSize: 13,
  },
  button: {
    padding: 12,
    fontSize: 16,
    backgroundColor: "#1E2A78",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 700,
  },
  dialogOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    backgroundColor: "rgba(15, 23, 42, 0.45)",
    boxSizing: "border-box",
  },
  dialogCard: {
    width: "100%",
    maxWidth: 420,
    padding: "28px 24px 24px",
    backgroundColor: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.22)",
    textAlign: "center",
    boxSizing: "border-box",
  },
  dialogIcon: {
    width: 46,
    height: 46,
    margin: "0 auto 14px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#ecfdf3",
    color: "#0a7c2f",
    fontSize: 24,
    fontWeight: 800,
  },
  dialogTitle: {
    margin: "0 0 10px",
    color: "#111827",
    fontSize: 22,
    fontWeight: 800,
  },
  dialogMessage: {
    margin: "0 0 22px",
    color: "#4b5563",
    fontSize: 15,
    lineHeight: 1.55,
    overflowWrap: "anywhere",
  },
  dialogButton: {
    minWidth: 120,
    padding: "11px 20px",
    fontSize: 15,
    backgroundColor: "#1E2A78",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    cursor: "pointer",
    fontWeight: 700,
  },
  footerLinks: {
    marginTop: 20,
    fontSize: 13,
    textAlign: "center",
  },
  footerLink: {
    color: "#1E2A78",
    textDecoration: "underline",
  },
};

export default Signup;
